import { observable, makeObservable } from 'mobx';

import type { EhrData } from 'chaika-frontend-types/frontend-cms';
import type { Class, Optional } from 'chaika-common-types';

import { Ehr } from '@patient/store/ehr/Ehr';
import { List } from '@store/abstract/List';

interface FilterParams {
    id?: string;
    executeId?: string;
    ownerId?: string;
    ownerIds?: string[];
}

export abstract class PatientListModel<
    M extends { id: string } = Ehr,
    D extends { id: string; mergedEhrIds?: string[]; mainEhrId?: string } = EhrData,
> extends List<M, D, FilterParams> {
    protected abstract modelClass: Class<Ehr>;
    @observable private mainEhrs: Map<string, string> = new Map();

    constructor(params?: D[]) {
        super(params);
        makeObservable(this);
    }

    public get(id: string): Optional<M> {
        const ehrId = this.has(id) ? id : this.getMainId(id);
        return super.get(ehrId);
    }

    public getMainId(ehrId: string): string {
        let mainEhrIdFind = false;
        let mainId = ehrId;
        let iterationCount = 0;

        while (!mainEhrIdFind) {
            iterationCount++;
            const targetId = this.mainEhrs.get(mainId);

            if (targetId) {
                if (iterationCount > 100) {
                    mainEhrIdFind = true;
                }
                mainId = targetId;
            } else {
                mainEhrIdFind = true;
            }
        }

        return mainId;
    }

    protected create(params: D): M {
        const mainEhrId = params.mainEhrId ?? params.id;
        if (mainEhrId !== params.id) {
            this.mainEhrs.set(params.id, mainEhrId);
        }
        (params.mergedEhrIds || []).forEach(sourceId => {
            if (sourceId !== mainEhrId) {
                this.mainEhrs.set(sourceId, mainEhrId);
            }
        });
        return new this.modelClass(params) as unknown as M;
    }

    protected filterValue(value: M, { id, executeId }: FilterParams): boolean {
        return (id ? value.id === id : true) && (executeId ? value.id !== executeId : true);
    }
}
